import { ExternalLink, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { SlowestJob } from "@/lib/analytics/types";
import { formatMs } from "@/lib/utils/format-duration";

interface SlowestJobsProps {
  jobs: SlowestJob[];
  loading: boolean;
  loaded: boolean;
  onLoad: () => void;
  disabled?: boolean;
}

export function SlowestJobs({ jobs, loading, loaded, onLoad, disabled }: SlowestJobsProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Slowest jobs</CardTitle>
          <CardDescription>Top 10 across the slowest 20 runs (lazy fetch)</CardDescription>
        </div>
        {!loaded ? (
          <Button size="sm" onClick={onLoad} disabled={disabled || loading}>
            {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
            Load jobs
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {!loaded ? (
          <p className="text-sm text-muted-foreground">
            Jobs are not fetched automatically to preserve API budget. Click
            <em> Load jobs</em> to analyze the top 20 slowest runs.
          </p>
        ) : jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No jobs found.</p>
        ) : (
          <ul className="divide-y text-sm">
            {jobs.map((j) => (
              <li key={j.jobId} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{j.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {j.workflowName ?? `Run #${j.runId}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {j.conclusion ? (
                    <Badge variant={j.conclusion === "success" ? "secondary" : "destructive"}>
                      {j.conclusion}
                    </Badge>
                  ) : null}
                  <span className="font-mono tabular-nums text-xs">{formatMs(j.durationMs)}</span>
                  {j.htmlUrl ? (
                    <a
                      href={j.htmlUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Open on GitHub"
                    >
                      <ExternalLink className="size-4" />
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
